import i18n from "i18next";
/**
 * Reviewing Ask Lilia's changes on a phone (Olivia, mobile reader 4, screens
 * 06–07). Lilia applies its edits at once and saves them, with a version taken
 * before ("Undo all" restores it). The reader then shows "Lilia changed N
 * blocks", steps through them, marks each in the margin, and shows a changed
 * block's before and after with "Undo this change". Keep clears the marks;
 * leaving without choosing counts as Keep.
 */
import type { ReaderBlock } from "./mobileReader";

/** Sent by Ask Lilia when an edit changed the document. */
export const AI_CHANGES_EVENT = "lilia:ai-changes";

export interface AiChangesDetail {
  documentId: string;
  changedBlockIds: string[];
  /** The version taken before the edit, for Undo all. */
  undoVersionId: string | null;
  /** The first sentence of Lilia's reply. */
  summary: string;
}

export type ChangeKind = "changed" | "added" | "removed";

export interface BlockChange {
  blockId: string;
  kind: ChangeKind;
  type: string;
  /** The block before the edit (null when Lilia added it). */
  before: ReaderBlock | null;
  /** The block after the edit (null when Lilia removed it). */
  after: ReaderBlock | null;
}

/** The first sentence of a reply, without Markdown, for the card's summary line. */
export function firstSentence(reply: string, max = 160): string {
  const text = reply
    .replace(/```[\s\S]*?```/g, " ")
    .replace(/[*_`#>]/g, "")
    .replace(/\s+/g, " ")
    .trim();
  const m = /^(.+?[.!?])(\s|$)/.exec(text);
  const s = m ? m[1] : text;
  return s.length > max ? `${s.slice(0, max - 1)}…` : s;
}

/**
 * The changes, in document order (removed blocks where they were), from the
 * blocks before the edit and after it. A block id Lilia reports that is the
 * same before and after is left out: nothing changed that a reader could see.
 */
export function buildChanges(before: readonly ReaderBlock[], after: readonly ReaderBlock[], changedIds: readonly string[]): BlockChange[] {
  const was = new Map(before.map((b) => [b.id, b]));
  const now = new Map(after.map((b) => [b.id, b]));
  const ids = new Set(changedIds);
  const out: BlockChange[] = [];
  const seen = new Set<string>();
  const push = (id: string) => {
    if (seen.has(id) || !ids.has(id)) return;
    seen.add(id);
    const b = was.get(id) ?? null;
    const a = now.get(id) ?? null;
    if (!b && !a) return;
    if (b && a && JSON.stringify(b.content) === JSON.stringify(a.content) && b.type === a.type) return;
    out.push({ blockId: id, kind: !b ? "added" : !a ? "removed" : "changed", type: (a ?? b)!.type, before: b, after: a });
  };
  // Document order: the blocks as they are now, with a removed block placed after its old predecessor.
  for (const a of after) push(a.id);
  for (const b of before) push(b.id);
  return out;
}

/** A block's readable text, for the before/after of prose. */
export function blockText(b: ReaderBlock | null): string {
  if (!b) return "";
  const c = b.content;
  const raw =
    typeof c.text === "string"
      ? c.text
      : typeof c.caption === "string"
        ? c.caption
        : typeof c.latex === "string"
          ? c.latex
          : Array.isArray(c.items)
            ? (c.items as unknown[]).map((i) => (typeof i === "string" ? i : typeof (i as { text?: unknown })?.text === "string" ? (i as { text: string }).text : "")).join("\n")
            : "";
  return raw.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").replace(/[ \t]+/g, " ").trim();
}

/** "Lilia changed 2 blocks", "Lilia added a block". */
export function changesHeadline(changes: readonly BlockChange[]): string {
  const n = changes.length;
  if (n === 1) {
    const k = changes[0].kind;
    if (k === "added") return i18n.t("mobile:reader.changes.added");
    if (k === "removed") return i18n.t("mobile:reader.changes.removed");
  }
  return i18n.t("mobile:reader.changes.changed", { count: n });
}

/**
 * References in raw block text as the document shows them: `\ref{fig:a}` → "1",
 * `\eqref{eq:b}` → "(2)", `\cite{k}` → "[k]". `labels` maps a label to the number
 * the page shows (read from the rendered references); an unknown one shows "??",
 * as LaTeX would.
 */
export function resolveRefs(text: string, labels: ReadonlyMap<string, string>): string {
  return text
    .replace(/\\(eqref|ref|cref|Cref|autoref)\{([^}]*)\}/g, (_, form: string, key: string) => {
      const n = labels.get(key.trim());
      const shown = n ?? "??";
      return form === "eqref" && !shown.startsWith("(") ? `(${shown})` : shown;
    })
    .replace(/\\cite[pt]?\{([^}]*)\}/g, (_, keys: string) => `[${keys.split(",").map((k) => k.trim()).join(", ")}]`);
}
